import { computed, inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';

import { Document } from '@documents/models/document-type';

import { ReviewerMaterialV2Service } from '@data-access/submissions';
import type {
  ReviewerMaterialUpdate,
  ReviewerMaterialsSignalStateType,
} from '../../types/reviewer-material.types';

/** Shared empty list, so a not-loaded read keeps a stable identity. */
const NO_MATERIALS: Document[] = [];

const REVIEWER_MATERIALS_DEFAULT_STATE: ReviewerMaterialsSignalStateType = {
  activeSubmissionId: null,
  error: null,
  isLoading: false,
  materials: NO_MATERIALS,
  resolvedSubmissionId: null,
};

/**
 * Drawer-scoped Reviewer Materials data state.
 *
 * The drawer decides when a list should load because it owns badge visibility;
 * this state owns API requests and applies their transitions with `patchState`.
 * The tab continues to own dialogs and other user-interface interactions.
 */
@Injectable()
export class ReviewerMaterialsSignalState {
  private readonly _state = signalState<ReviewerMaterialsSignalStateType>(
    REVIEWER_MATERIALS_DEFAULT_STATE,
  );
  private readonly _reviewerMaterialV2Service = inject(
    ReviewerMaterialV2Service,
  );

  /**
   * Materials resolved for the active submission. Materials from a previous
   * submission remain hidden while the next submission loads.
   */
  readonly $materials = computed<Document[]>(() => {
    const activeSubmissionId = this._state.activeSubmissionId();
    return activeSubmissionId &&
      activeSubmissionId === this._state.resolvedSubmissionId()
      ? this._state.materials()
      : NO_MATERIALS;
  });

  /** True while the active submission's materials request is in flight. */
  readonly $isLoading = computed(() => this._state.isLoading());

  /** The active submission's latest materials-load error, if any. */
  readonly $error = computed(() => this._state.error());

  /**
   * Loads the active submission's reviewer materials into this state.
   *
   * A later load cancels the previous request. Rows from a resolved refresh
   * remain available if the next request for that same submission fails.
   *
   * @param request - Submission whose materials should load, or null to clear.
   */
  readonly loadReviewerMaterials = rxMethod<{ submissionId: string | null }>(
    pipe(
      tap(({ submissionId }) => {
        if (!submissionId) {
          patchState(this._state, REVIEWER_MATERIALS_DEFAULT_STATE);
          return;
        }

        patchState(this._state, {
          activeSubmissionId: submissionId,
          error: null,
          isLoading: true,
        });
      }),
      switchMap(({ submissionId }) => {
        if (!submissionId) return EMPTY;

        return this._reviewerMaterialV2Service
          .getAllReviewMaterials(submissionId)
          .pipe(
            tapResponse({
              next: (response) => {
                if (this._state.activeSubmissionId() !== submissionId) return;

                patchState(this._state, {
                  error: null,
                  isLoading: false,
                  materials: response.data ?? [],
                  resolvedSubmissionId: submissionId,
                });
              },
              error: (error: unknown) => {
                if (this._state.activeSubmissionId() !== submissionId) return;

                patchState(this._state, { error, isLoading: false });
              },
            }),
          );
      }),
    ),
  );

  /**
   * Persists a material's completion status and updates the resolved list.
   *
   * @param request - Material and completion status selected by the user.
   */
  readonly toggleCompleteReviewMaterial = rxMethod<{
    material: Document;
    isComplete: boolean;
  }>(
    pipe(
      switchMap(({ material, isComplete }) => {
        const submissionId = this._state.activeSubmissionId();

        return this._reviewerMaterialV2Service
          .toggleCompleteReviewMaterial(material.id, isComplete)
          .pipe(
            tapResponse({
              next: (document) => this._replaceMaterial(document, submissionId),
              error: () => undefined,
            }),
          );
      }),
    ),
  );

  /**
   * Persists a material's document type and updates the resolved list.
   *
   * @param request - Material and document type selected by the user.
   */
  readonly updateReviewMaterial = rxMethod<{
    material: Document;
    update: ReviewerMaterialUpdate;
  }>(
    pipe(
      switchMap(({ material, update }) => {
        const submissionId = this._state.activeSubmissionId();

        return this._reviewerMaterialV2Service
          .updateReviewMaterial(material.id, update)
          .pipe(
            tapResponse({
              next: (document) => this._replaceMaterial(document, submissionId),
              error: () => undefined,
            }),
          );
      }),
    ),
  );

  /**
   * Deletes a material and removes it from the resolved list.
   *
   * @param material - Material selected for deletion.
   */
  readonly removeReviewMaterial = rxMethod<Document>(
    pipe(
      switchMap((material) => {
        const submissionId = this._state.activeSubmissionId();

        return this._reviewerMaterialV2Service
          .deleteReviewMaterial(material.id)
          .pipe(
            tapResponse({
              next: () => this._removeMaterial(material.id, submissionId),
              error: () => undefined,
            }),
          );
      }),
    ),
  );

  /** Replaces one material after a successful state-owned write. */
  private _replaceMaterial(
    document: Document,
    submissionId: string | null,
  ): void {
    if (
      !submissionId ||
      this._state.activeSubmissionId() !== submissionId ||
      this._state.resolvedSubmissionId() !== submissionId
    ) {
      return;
    }

    patchState(this._state, (state) => ({
      materials: state.materials.map((material) =>
        material.id === document.id ? document : material,
      ),
    }));
  }

  /**
   * Removes one material after a successful state-owned delete.
   */
  private _removeMaterial(
    documentId: string,
    submissionId: string | null,
  ): void {
    if (
      !submissionId ||
      this._state.activeSubmissionId() !== submissionId ||
      this._state.resolvedSubmissionId() !== submissionId
    ) {
      return;
    }

    patchState(this._state, (state) => ({
      materials: state.materials.filter(
        (material) => material.id !== documentId,
      ),
    }));
  }
}
